/**
 * Sysadmin Models — Runtime monitor (Wave 3 / M6c Phase E).
 *
 * Criteria 6 + 7: manual load/unload + hardware monitor. Aggregates
 *   - host CPU / RAM / disk / load avg / uptime
 *   - GPU info (NVIDIA `nvidia-smi`-visible cards; for Strix Halo +
 *     other AMD iGPUs the panel shows "no GPUs detected" until the
 *     ROCm probe is wired in M6e)
 *   - LiteLLM gateway health + visible brand list (does the gateway
 *     see what we registered?)
 *   - Currently-registered engines + which gateway shows + per-engine
 *     load / unload controls (audited)
 *
 * Refresh: TanStack Query polls every 10 s while the page is in
 * focus. All probes are best-effort; partial snapshots render rather
 * than blocking the page.
 *
 * Per I-M6.7 this view exposes engine internals (backend_model_name,
 * baseLlmKey) — sysadmin-only. Tenant chat UI never sees these
 * fields.
 */

import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import {
  HardDrive,
  Cpu,
  MemoryStick,
  Database,
  Activity,
  Network,
  Zap,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  RefreshCw,
  Loader2,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useQueryClient } from '@tanstack/react-query';
import {
  useRuntimeHardware,
  useLoadEngine,
  useUnloadEngine,
  type RuntimeHardwareSnapshot,
} from '@/lib/use-argus-models';
import { ModelsSubNav } from './models/index';

export const Route = createFileRoute('/sysadmin/models/runtime')({
  component: RuntimePage,
});

function RuntimePage() {
  const qc = useQueryClient();
  const hwQuery = useRuntimeHardware();

  return (
    <div className="p-6 max-w-[1400px] mx-auto space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
            <HardDrive className="h-6 w-6" />
            Runtime &amp; Hardware
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Live host + GPU + gateway snapshot. Auto-refreshes every 10 s.
            Use the per-engine controls to manually load or unload models.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() =>
            qc.invalidateQueries({ queryKey: ['admin', 'argus-runtime', 'hardware'] })
          }
          disabled={hwQuery.isFetching}
        >
          <RefreshCw
            className={`h-4 w-4 mr-1.5 ${hwQuery.isFetching ? 'animate-spin' : ''}`}
          />
          Refresh
        </Button>
      </div>

      <ModelsSubNav active="/sysadmin/models/runtime" />

      {hwQuery.isLoading ? (
        <Card>
          <CardContent className="py-12 text-center space-y-2">
            <Loader2 className="h-8 w-8 mx-auto animate-spin text-muted-foreground" />
            <p className="text-sm text-muted-foreground">Probing host…</p>
          </CardContent>
        </Card>
      ) : hwQuery.isError || !hwQuery.data ? (
        <Card>
          <CardContent className="py-12 text-center space-y-3">
            <XCircle className="h-10 w-10 text-destructive mx-auto" />
            <h2 className="text-lg font-semibold">Snapshot probe failed</h2>
            <p className="text-sm text-muted-foreground max-w-md mx-auto">
              The runtime probe could not reach the API. Check server logs +
              verify the platform-admin JWT is valid.
            </p>
          </CardContent>
        </Card>
      ) : (
        <RuntimeContent snapshot={hwQuery.data} />
      )}
    </div>
  );
}

function RuntimeContent({ snapshot }: { snapshot: RuntimeHardwareSnapshot }) {
  return (
    <>
      <CollectedAtRow collectedAt={snapshot.collectedAt} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <HostPanel host={snapshot.host} />
        <GpuPanel gpu={snapshot.gpu} />
      </div>

      <GatewayPanel gateway={snapshot.gateway} />

      <EnginesPanel
        engines={snapshot.engines}
        gatewayBrands={snapshot.gateway.brands}
      />
    </>
  );
}

function CollectedAtRow({ collectedAt }: { collectedAt: string }) {
  return (
    <div className="text-xs text-muted-foreground flex items-center gap-1.5">
      <Activity className="h-3 w-3" />
      Snapshot collected {new Date(collectedAt).toLocaleTimeString()}
    </div>
  );
}

function HostPanel({ host }: { host: RuntimeHardwareSnapshot['host'] }) {
  const memUsedPct =
    host.memoryTotalMb && host.memoryUsedMb
      ? Math.round((host.memoryUsedMb / host.memoryTotalMb) * 100)
      : null;
  const diskUsedPct =
    host.diskTotalGb && host.diskUsedGb
      ? Math.round((host.diskUsedGb / host.diskTotalGb) * 100)
      : null;

  const uptimeDays = Math.floor(host.uptimeSeconds / 86400);
  const uptimeHours = Math.floor((host.uptimeSeconds % 86400) / 3600);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Cpu className="h-4 w-4" />
          Host
          {host.available ? (
            <Badge variant="outline" className="ml-auto bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/30">
              <CheckCircle2 className="h-3 w-3 mr-1" />
              available
            </Badge>
          ) : (
            <Badge variant="outline" className="ml-auto">
              <XCircle className="h-3 w-3 mr-1" />
              unavailable
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
          <Field label="Hostname" value={host.hostname} mono />
          <Field label="OS" value={host.os} />
          <Field label="Kernel" value={host.kernel} mono />
          <Field label="CPU cores" value={String(host.cpuCores)} />
          <Field
            label="CPU model"
            value={host.cpuModel ?? '—'}
            wide
            mono
          />
          <Field
            label="Uptime"
            value={`${uptimeDays}d ${uptimeHours}h`}
          />
          <Field
            label="Load avg"
            value={host.loadAvg.map((n) => n.toFixed(2)).join(' / ')}
            mono
          />
        </div>

        {host.memoryTotalMb !== null && memUsedPct !== null && (
          <div>
            <div className="flex items-center justify-between text-xs mb-1">
              <span className="flex items-center gap-1.5">
                <MemoryStick className="h-3 w-3" />
                Memory
              </span>
              <span className="font-mono">
                {Math.round((host.memoryUsedMb ?? 0) / 1024)} /{' '}
                {Math.round(host.memoryTotalMb / 1024)} GiB ({memUsedPct}%)
              </span>
            </div>
            <Progress value={memUsedPct} className="h-1.5" />
          </div>
        )}

        {host.diskTotalGb !== null && diskUsedPct !== null && (
          <div>
            <div className="flex items-center justify-between text-xs mb-1">
              <span className="flex items-center gap-1.5">
                <Database className="h-3 w-3" />
                Disk
              </span>
              <span className="font-mono">
                {Math.round(host.diskUsedGb ?? 0)} / {Math.round(host.diskTotalGb)} GB
                ({diskUsedPct}%)
              </span>
            </div>
            <Progress value={diskUsedPct} className="h-1.5" />
          </div>
        )}

        {host.notes.length > 0 && (
          <div className="rounded border border-amber-500/30 bg-amber-500/5 p-2 text-xs">
            <div className="flex items-start gap-1.5">
              <AlertTriangle className="h-3 w-3 text-amber-600 mt-0.5 flex-shrink-0" />
              <div className="text-amber-700 dark:text-amber-300">
                {host.notes.join(' · ')}
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function GpuPanel({ gpu }: { gpu: RuntimeHardwareSnapshot['gpu'] }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Zap className="h-4 w-4" />
          GPU
          {gpu.available ? (
            <Badge variant="outline" className="ml-auto bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/30">
              {gpu.gpus.length} card{gpu.gpus.length === 1 ? '' : 's'}
            </Badge>
          ) : (
            <Badge variant="outline" className="ml-auto">
              CPU-only
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        {!gpu.available ? (
          <div className="text-xs text-muted-foreground">
            {gpu.unavailableReason ?? 'No GPUs detected'}. CPU-only is a
            supported deployment profile (CLAUDE.md §15.2). Strix Halo iGPU
            probing arrives in M6e.
          </div>
        ) : (
          gpu.gpus.map((g) => {
            const vramPct =
              g.vramTotalMb && g.vramUsedMb
                ? Math.round((g.vramUsedMb / g.vramTotalMb) * 100)
                : null;
            return (
              <div key={g.index} className="space-y-2 border-l-2 border-emerald-500/30 pl-3">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium">{g.name}</div>
                    <div className="text-xs text-muted-foreground font-mono">
                      slot {g.index}
                    </div>
                  </div>
                  {g.temperatureC !== null && (
                    <Badge variant="outline" className="font-mono text-xs">
                      {g.temperatureC}°C
                    </Badge>
                  )}
                </div>

                {vramPct !== null && (
                  <div>
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span>VRAM</span>
                      <span className="font-mono">
                        {Math.round((g.vramUsedMb ?? 0) / 1024)} /{' '}
                        {Math.round((g.vramTotalMb ?? 0) / 1024)} GiB ({vramPct}%)
                      </span>
                    </div>
                    <Progress value={vramPct} className="h-1.5" />
                  </div>
                )}

                {g.utilizationPercent !== null && (
                  <div>
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span>Utilization</span>
                      <span className="font-mono">{g.utilizationPercent}%</span>
                    </div>
                    <Progress value={g.utilizationPercent} className="h-1.5" />
                  </div>
                )}
              </div>
            );
          })
        )}
      </CardContent>
    </Card>
  );
}

function GatewayPanel({ gateway }: { gateway: RuntimeHardwareSnapshot['gateway'] }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Network className="h-4 w-4" />
          LiteLLM Gateway
          {gateway.healthy ? (
            <Badge variant="outline" className="ml-auto bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/30">
              <CheckCircle2 className="h-3 w-3 mr-1" />
              healthy
            </Badge>
          ) : (
            <Badge variant="outline" className="ml-auto bg-red-500/10 text-red-700 dark:text-red-300 border-red-500/30">
              <XCircle className="h-3 w-3 mr-1" />
              unhealthy
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        {gateway.probeError ? (
          <div className="rounded border border-red-500/30 bg-red-500/5 p-2 text-xs">
            <div className="flex items-start gap-1.5">
              <AlertTriangle className="h-3 w-3 text-red-600 mt-0.5 flex-shrink-0" />
              <div className="text-red-700 dark:text-red-300 font-mono">
                {gateway.probeError}
              </div>
            </div>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground">Brands visible:</span>
              <span className="font-mono">{gateway.brandCount}</span>
            </div>
            {gateway.brands.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {gateway.brands.map((b) => (
                  <Badge key={b} variant="outline" className="font-mono text-xs">
                    {b}
                  </Badge>
                ))}
              </div>
            )}
            <p className="text-xs text-muted-foreground">
              Per ADR-0020 the gateway is the single legal dispatch path for
              all brands. Brands missing from this list cannot be reached from
              the request path even if the DB row exists.
            </p>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function EnginesPanel({
  engines,
  gatewayBrands,
}: {
  engines: RuntimeHardwareSnapshot['engines'];
  gatewayBrands: string[];
}) {
  const [confirmAction, setConfirmAction] = useState<{
    kind: 'load' | 'unload';
    argusModelId: string;
    engineId: string;
    brandId: string;
    backendModelName: string;
  } | null>(null);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Activity className="h-4 w-4" />
          Registered engines
          <Badge variant="outline" className="ml-auto">
            {engines.total} active
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        {engines.registered.length === 0 ? (
          <div className="py-12 text-center text-sm text-muted-foreground">
            No engines registered. Use the catalog browser to add a brand +
            engine pool.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[140px]">Brand</TableHead>
                  <TableHead className="w-[200px]">Backend model</TableHead>
                  <TableHead className="w-[180px]">Base LLM</TableHead>
                  <TableHead className="w-[80px]">VRAM</TableHead>
                  <TableHead className="w-[100px]">Default</TableHead>
                  <TableHead className="w-[140px]">Gateway</TableHead>
                  <TableHead className="w-[160px] text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {engines.registered.map((e) => (
                  <TableRow key={e.engineId}>
                    <TableCell>
                      <Badge variant="outline" className="font-mono text-xs">
                        {e.brandId}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <span className="font-mono text-xs">{e.backendModelName}</span>
                    </TableCell>
                    <TableCell>
                      <span className="font-mono text-xs">{e.baseLlmKey}</span>
                    </TableCell>
                    <TableCell>
                      {e.vramGb !== null ? (
                        <span className="font-mono text-xs">{e.vramGb} GB</span>
                      ) : (
                        <span className="text-muted-foreground text-xs">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {e.isDefault ? (
                        <Badge
                          variant="outline"
                          className="bg-blue-500/10 text-blue-700 dark:text-blue-300 border-blue-500/30 text-xs"
                        >
                          default
                        </Badge>
                      ) : (
                        <span className="text-muted-foreground text-xs">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {e.visibleAtGateway ? (
                        <Badge
                          variant="outline"
                          className="bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/30 text-xs"
                        >
                          <CheckCircle2 className="h-3 w-3 mr-1" />
                          visible
                        </Badge>
                      ) : (
                        <Badge
                          variant="outline"
                          className="bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/30 text-xs"
                          title="Brand row exists in DB but gateway does not advertise it"
                        >
                          <AlertTriangle className="h-3 w-3 mr-1" />
                          missing
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          title="Force load this engine"
                          onClick={() =>
                            setConfirmAction({
                              kind: 'load',
                              argusModelId: e.argusModelId,
                              engineId: e.engineId,
                              brandId: e.brandId,
                              backendModelName: e.backendModelName,
                            })
                          }
                        >
                          Load
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          title="Request unload — llama-swap honors TTL eviction"
                          onClick={() =>
                            setConfirmAction({
                              kind: 'unload',
                              argusModelId: e.argusModelId,
                              engineId: e.engineId,
                              brandId: e.brandId,
                              backendModelName: e.backendModelName,
                            })
                          }
                        >
                          Unload
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>

      {gatewayBrands.length > 0 && engines.registered.some((e) => !e.visibleAtGateway) && (
        <CardContent className="border-t pt-3 pb-3">
          <div className="flex items-start gap-2 text-xs text-amber-700 dark:text-amber-300">
            <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
            <div>
              One or more registered brands are not advertised by the
              gateway. The gateway config or the engine's
              <span className="font-mono">backend_model_name</span> may be
              stale. Inspect <span className="font-mono">/v1/models</span> on
              the gateway side and re-register the engine.
            </div>
          </div>
        </CardContent>
      )}

      {confirmAction && (
        <ConfirmEngineAction
          action={confirmAction}
          onClose={() => setConfirmAction(null)}
        />
      )}
    </Card>
  );
}

function Field({
  label,
  value,
  mono,
  wide,
}: {
  label: string;
  value: string;
  mono?: boolean;
  wide?: boolean;
}) {
  return (
    <div className={wide ? 'col-span-2' : ''}>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className={`text-sm ${mono ? 'font-mono text-xs' : ''} truncate`}>
        {value}
      </div>
    </div>
  );
}

function ConfirmEngineAction({
  action,
  onClose,
}: {
  action: {
    kind: 'load' | 'unload';
    argusModelId: string;
    engineId: string;
    brandId: string;
    backendModelName: string;
  };
  onClose: () => void;
}) {
  const loadMutation = useLoadEngine();
  const unloadMutation = useUnloadEngine();
  const mutation = action.kind === 'load' ? loadMutation : unloadMutation;

  const handleConfirm = async () => {
    await mutation.mutateAsync({
      argusModelId: action.argusModelId,
      engineId: action.engineId,
    });
    onClose();
  };

  const isLoad = action.kind === 'load';

  return (
    <AlertDialog open onOpenChange={(o) => !o && onClose()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {isLoad ? 'Force load engine?' : 'Request engine unload?'}
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-2">
              <p>
                Brand:{' '}
                <Badge variant="outline" className="font-mono text-xs">
                  {action.brandId}
                </Badge>
              </p>
              <p>
                Backend:{' '}
                <span className="font-mono text-xs">{action.backendModelName}</span>
              </p>
              {isLoad ? (
                <p className="text-xs">
                  This sends a primer request to llama-swap. Returns 202
                  immediately; the actual load runs async + is audited
                  regardless of outcome.
                </p>
              ) : (
                <p className="text-xs">
                  v1 has no immediate-unload RPC — llama-swap honors TTL
                  eviction (10 min for warm-set). The request is audited and
                  the engine will be unloaded at the next eviction cycle.
                </p>
              )}
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={mutation.isPending}>
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => {
              e.preventDefault();
              handleConfirm();
            }}
            disabled={mutation.isPending}
          >
            {mutation.isPending ? (
              <>
                <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                Sending…
              </>
            ) : isLoad ? (
              'Load'
            ) : (
              'Unload'
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
